import * as React from "react";
import {
  List, Datagrid, TextField, BooleanField, NumberField,
  Show, TabbedShowLayout, TextInput, BooleanInput, useRecordContext,
} from "react-admin";
import { Box } from "@mui/material";
import { JsonField } from "../../components/JsonField";
import { RemoteList, fmtDate } from "../../components/RemoteList";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";

const pick = (row, ...keys) => {
  for (const k of keys) {
    const parts = k.split(".");
    let v = row;
    for (const p of parts) v = v == null ? undefined : v[p];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

// Lecture seule : les écritures (PUT/PATCH cancel/DELETE/POST) sont réservées
// à l'organisateur côté backend (403 pour l'admin). Champs = EventDTO.
// Filtres GET /events supportés : organizerId, proId, upcomingOnly, …
const filters = [
  <TextInput key="organizerId" source="organizerId" label="ID organisateur" alwaysOn />,
  <BooleanInput key="upcomingOnly" source="upcomingOnly" label="À venir uniquement" />,
];

const AttendeesList = () => {
  const r = useRecordContext();
  const [attendees, setAttendees] = React.useState([]);
  React.useEffect(() => {
    if (!r?.id) return;
    httpClient(`${API_BASE_URL}/events/${r.id}/attendees`)
      .then(({ json }) => setAttendees(Array.isArray(json) ? json : json?.data || []))
      .catch(() => setAttendees([]));
  }, [r?.id]);
  if (!attendees.length) return <Box sx={{ p: 2, color: "text.secondary" }}>Aucun participant</Box>;
  return (
    <Box component="pre" sx={{
      bgcolor: "#f5f7fa", p: 2, fontSize: 12, maxHeight: 400, overflow: "auto",
    }}>
      {JSON.stringify(attendees, null, 2)}
    </Box>
  );
};

export default {
  list: () => (
    <List filters={filters} sort={{ field: "startAt", order: "DESC" }}>
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="title" />
        <TextField source="audience" label="Public" />
        <TextField source="city" label="Ville" />
        <FrDateField source="startAt" showTime />
        <NumberField locales="fr-FR" source="attendeeCount" label="Inscrits" />
        <NumberField locales="fr-FR" source="maxAttendees" label="Places" />
        <BooleanField source="cancelled" label="Annulé" />
      </Datagrid>
    </List>
  ),
  show: () => (
    <Show>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Détails">
          <TextField source="title" />
          <TextField source="description" />
          <TextField source="organizerName" label="Organisateur" />
          <TextField source="audience" label="Public" />
          <TextField source="city" label="Ville" />
          <TextField source="location" />
          <FrDateField source="startAt" showTime />
          <FrDateField source="endAt" showTime />
          <NumberField locales="fr-FR" source="attendeeCount" label="Inscrits" />
          <NumberField locales="fr-FR" source="maxAttendees" label="Places" />
          <NumberField locales="fr-FR" source="waitlistCount" label="Liste d'attente" />
          <TextField source="priceLabel" label="Prix" />
          <BooleanField source="cancelled" label="Annulé" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Participants">
          <AttendeesList />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Commentaires">
          <RemoteList
            buildPath={(r) => `/events/${r.id}/comments`}
            emptyText="Aucun commentaire"
            columns={[
              { key: "content", label: "Commentaire", render: (c) => pick(c, "content") || "—" },
              { key: "author", label: "Auteur", render: (c) => pick(c, "authorName", "authorId") || "—" },
              { key: "date", label: "Date", render: (c) => fmtDate(pick(c, "createdAt")) },
            ]}
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Brut">
          <JsonField source="__all__" />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  ),
};
